// Visit Wizard Context - State management for multi-step visit workflow
import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react'
import {
  VisitWizardState,
  WizardAction,
  WizardStep,
  Client,
  Classification,
  Assistance,
  DenialReason,
  AssistanceType,
} from '../types/visit-wizard'

// Initial state
const initialState: VisitWizardState = {
  currentStep: 'client-selection',
  stepHistory: [],
  completedSteps: new Set(),
  selectedClient: null,
  classification: null,
  denialReason: null,
  assistances: [],
  currentAssistanceType: null,
  visitId: null,
  existingControlNumbers: [],
  isSubmitting: false,
  errors: {},
}

// Reducer function
function wizardReducer(state: VisitWizardState, action: WizardAction): VisitWizardState {
  switch (action.type) {
    case 'SET_STEP': {
      const newHistory = [...state.stepHistory, state.currentStep]
      return {
        ...state,
        currentStep: action.payload,
        stepHistory: newHistory,
        completedSteps: new Set([...state.completedSteps, state.currentStep]),
      }
    }

    case 'GO_BACK': {
      const newHistory = [...state.stepHistory]
      const previousStep = newHistory.pop()
      return {
        ...state,
        currentStep: previousStep || state.currentStep,
        stepHistory: newHistory,
      }
    }

    case 'SELECT_CLIENT':
      return {
        ...state,
        selectedClient: action.payload,
      }

    case 'SET_CLASSIFICATION': {
      // Route based on decision field
      const nextStep = action.payload.decision === 'deny' ? 'denial' : 'assistance-selection'
      return {
        ...state,
        classification: action.payload,
        currentStep: nextStep,
      }
    }

    case 'SET_DENIAL_REASON':
      return {
        ...state,
        denialReason: action.payload,
      }

    case 'SELECT_ASSISTANCE_TYPE':
      return {
        ...state,
        currentAssistanceType: action.payload,
        currentStep: 'assistance-form',
      }

    case 'ADD_ASSISTANCE': {
      return {
        ...state,
        assistances: [...state.assistances, action.payload],
      }
    }

    case 'UPDATE_ASSISTANCE': {
      return {
        ...state,
        assistances: state.assistances.map((assistance) =>
          assistance.id === action.payload.id
            ? { ...assistance, ...action.payload.data }
            : assistance
        ),
      }
    }

    case 'REMOVE_ASSISTANCE':
      return {
        ...state,
        assistances: state.assistances.filter((a) => a.id !== action.payload),
      }

    case 'SET_VISIT_ID':
      return {
        ...state,
        visitId: action.payload,
      }

    case 'SET_EXISTING_CONTROL_NUMBERS':
      return {
        ...state,
        existingControlNumbers: action.payload,
      }

    case 'SET_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.field]: action.payload.message,
        },
      }

    case 'CLEAR_ERRORS':
      return {
        ...state,
        errors: {},
      }

    case 'SET_SUBMITTING':
      return {
        ...state,
        isSubmitting: action.payload,
      }

    case 'RESET_WIZARD':
      return initialState

    default:
      return state
  }
}

// Context value interface
interface VisitWizardContextValue {
  state: VisitWizardState
  dispatch: React.Dispatch<WizardAction>
  // Helper functions
  goToStep: (step: WizardStep) => void
  goBack: () => void
  selectClient: (client: Client) => void
  setClassification: (classification: Classification) => void
  setDenialReason: (reason: DenialReason) => void
  selectAssistanceType: (type: AssistanceType) => void
  addAssistance: (assistance: Assistance) => void
  updateAssistance: (id: string, data: Partial<Assistance>) => void
  removeAssistance: (id: string) => void
  setVisitId: (id: string) => void
  setExistingControlNumbers: (controlNumbers: string[]) => void
  setError: (field: string, message: string) => void
  clearErrors: () => void
  resetWizard: () => void
  canProceed: boolean
}

// Create context
const VisitWizardContext = createContext<VisitWizardContextValue | undefined>(undefined)

// Provider component
export function VisitWizardProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(wizardReducer, initialState)

  // Helper functions
  const goToStep = useCallback((step: WizardStep) => {
    dispatch({ type: 'SET_STEP', payload: step })
  }, [])

  const goBack = useCallback(() => {
    dispatch({ type: 'GO_BACK' })
  }, [])

  const selectClient = useCallback((client: Client) => {
    dispatch({ type: 'SELECT_CLIENT', payload: client })
    dispatch({ type: 'SET_STEP', payload: 'client-review' })
  }, [])

  const setClassification = useCallback((classification: Classification) => {
    // Routes to assistance-selection after classification
    dispatch({ type: 'SET_CLASSIFICATION', payload: classification })
  }, [])

  const setDenialReason = useCallback((reason: DenialReason) => {
    dispatch({ type: 'SET_DENIAL_REASON', payload: reason })
  }, [])

  const selectAssistanceType = useCallback((type: AssistanceType) => {
    dispatch({ type: 'SELECT_ASSISTANCE_TYPE', payload: type })
  }, [])

  const addAssistance = useCallback((assistance: Assistance) => {
    dispatch({ type: 'ADD_ASSISTANCE', payload: assistance })
  }, [])

  const updateAssistance = useCallback((id: string, data: Partial<Assistance>) => {
    dispatch({ type: 'UPDATE_ASSISTANCE', payload: { id, data } })
  }, [])

  const removeAssistance = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_ASSISTANCE', payload: id })
  }, [])

  const setVisitId = useCallback((id: string) => {
    dispatch({ type: 'SET_VISIT_ID', payload: id })
  }, [])

  const setExistingControlNumbers = useCallback((controlNumbers: string[]) => {
    dispatch({ type: 'SET_EXISTING_CONTROL_NUMBERS', payload: controlNumbers })
  }, [])

  const setError = useCallback((field: string, message: string) => {
    dispatch({ type: 'SET_ERROR', payload: { field, message } })
  }, [])

  const clearErrors = useCallback(() => {
    dispatch({ type: 'CLEAR_ERRORS' })
  }, [])

  const resetWizard = useCallback(() => {
    dispatch({ type: 'RESET_WIZARD' })
  }, [])

  // Validation logic for proceeding to next step
  const canProceed = useMemo(() => {
    switch (state.currentStep) {
      case 'client-selection':
        return state.selectedClient !== null
      case 'client-review':
        return state.selectedClient !== null
      case 'classification':
        return state.classification !== null
      case 'denial':
        return state.denialReason !== null
      case 'assistance-selection':
        return state.currentAssistanceType !== null || state.assistances.length > 0
      case 'assistance-form':
        return state.assistances.length > 0
      case 'summary':
        return state.assistances.every((a) => a.is_saved)
      default:
        return true
    }
  }, [state])

  const value: VisitWizardContextValue = {
    state,
    dispatch,
    goToStep,
    goBack,
    selectClient,
    setClassification,
    setDenialReason,
    selectAssistanceType,
    addAssistance,
    updateAssistance,
    removeAssistance,
    setVisitId,
    setExistingControlNumbers,
    setError,
    clearErrors,
    resetWizard,
    canProceed,
  }

  return (
    <VisitWizardContext.Provider value={value}>
      {children}
    </VisitWizardContext.Provider>
  )
}

// Custom hook to use the wizard context
export function useVisitWizard() {
  const context = useContext(VisitWizardContext)
  if (!context) {
    throw new Error('useVisitWizard must be used within VisitWizardProvider')
  }
  return context
}
